import React, { useCallback } from "react";
import { Alert, Button, notification, Popconfirm, Space, Tag } from "antd";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import {
  deleteProjectById,
  getProjectById,
  toggleArchivedById,
  togglePublishedById,
} from "../../store/api/projects";
import ContentForm from "./ContentForm";
import AddProjectPopup from "./AddProjectPopup";
import { ProjectStatus } from "./ProjectStatus";
import { ProjectThumbnail } from "./ProjectThumbnail";
import ProjectGallery from "./ProjectGallery";

const ProjectShow = () => {
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);

  const [project, setProject] = useState(null);

  const { id } = useParams();
  const history = useHistory();

  const fetchData = useCallback(() => {
    setLoading(true);

    getProjectById(id)
      .then(({ data }) => {
        setLoading(false);
        setProject(data);
      })
      .catch((error) => {
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleArchive = (item) => {
    setArchiveLoading(true);
    toggleArchivedById(item.id)
      .then(({ data }) => {
        setDeleteLoading(false);
        notification.success({ message: data.message });
        fetchData();
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
        setDeleteLoading(false);
      });
  };

  const handleDelete = (item) => {
    setDeleteLoading(true);
    deleteProjectById(item.id)
      .then(({ data }) => {
        setDeleteLoading(false);
        notification.success({ message: data.message });
        history.push("/projects");
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
        setDeleteLoading(false);
      });
  };

  const changeStatus = (item) => {
    setPublishLoading(true);
    togglePublishedById(item.id)
      .then(({ data }) => {
        setPublishLoading(false);
        notification.success({
          message: project.isPublished
            ? "Project unpublished succesfully"
            : "Project published successfully",
        });
        fetchData();
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
        setPublishLoading(false);
      });
  };

  if (loading || !project) {
    return (
      <div className="page-wrapper">
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper project-show">
        <Space
          direction="horizontal"
          style={{
            width: "100%",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}
          wrap
        >
          <div
            style={{
              marginBottom: "1rem",
            }}
          >
            <h2 style={{ fontWeight: "bold" }}>
              {project.title} {project.isArchived && <Tag>Archived</Tag>}
            </h2>

            <Alert
              message={
                project.isPublished
                  ? "The project is published to www."
                  : "The project is not published to www."
              }
              type={project.isPublished ? "success" : "warning"}
              action={
                <Space>
                  <Popconfirm
                    confirmLoading={publishLoading}
                    title={
                      project.isPublished
                        ? "Are you sure want to unpublish this project?"
                        : "Are you sure want to publish this project?"
                    }
                    onConfirm={() => changeStatus(project)}
                  >
                    <Button
                      style={{ width: "100px" }}
                      danger={project.isPublished}
                    >
                      {project.isPublished ? "Unpublish" : "Publish"}
                    </Button>
                  </Popconfirm>
                </Space>
              }
            />
          </div>
          <Space
            style={{
              width: "100%",
              marginBottom: "15px",
              justifyContent: "flex-end",
            }}
            size={10}
            direction="horizontal"
            align="start"
          >
            <Popconfirm
              confirmLoading={archiveLoading}
              title={
                project.isArchived
                  ? "Are you sure want to unarchive this project?"
                  : "Are you sure want to archive this project?"
              }
              onConfirm={() => handleArchive(project)}
            >
              <Button type="secondary">
                {project.isArchived ? "Unarchive" : "Archive"}
              </Button>
            </Popconfirm>
            <Popconfirm
              confirmLoading={deleteLoading}
              title="Are you sure want to delete this project?"
              onConfirm={() => handleDelete(project)}
            >
              <Button type="danger">Delete</Button>
            </Popconfirm>

            <Button
              type="primary"
              onClick={() => {
                setIsModalVisible(true);
              }}
            >
              Edit Info
            </Button>
          </Space>
        </Space>

        <div
          style={{
            display: "flex",
            gap: "1rem",
            width: "100%",
            marginBottom: "2rem",
            flexWrap: "wrap",
          }}
        >
          <section style={{ marginRight: "2rem" }}>
            <h3 style={{ fontWeight: "bold" }}>Status</h3>
            <ProjectStatus status={project.status} />
          </section>

          <section>
            <h3 style={{ fontWeight: "bold" }}>Project Date</h3>
            {project.projectDate
              ? new Date(project.projectDate).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "-"}
          </section>
        </div>
        <section style={{ marginBottom: "2rem" }}>
          <h3 style={{ fontWeight: "bold" }}>Studies</h3>
          {project.tags &&
            project.tags.split("#").map((tag) => <Tag key={tag}>{tag}</Tag>)}
        </section>
        <section className="project-thumbnail" style={{ marginBottom: "2rem" }}>
          <h3 style={{ fontWeight: "bold" }}>Thumbnail</h3>
          <div>
            <ProjectThumbnail project={project} />
          </div>
        </section>
        <section style={{ marginBottom: "2rem" }}>
          <h3 style={{ fontWeight: "bold" }}>Content</h3>
          <ContentForm project={project}></ContentForm>
        </section>
        <section style={{ marginBottom: "2rem" }}>
          <h3 style={{ fontWeight: "bold" }}>Gallery</h3>
          <div>
            <ProjectGallery project={project} />
          </div>
        </section>
      </div>
      {isModalVisible && (
        <AddProjectPopup
          closePopup={() => setIsModalVisible(false)}
          item={project}
          onUpdateSuccess={() => fetchData()}
        />
      )}
    </>
  );
};

export default ProjectShow;
